import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const frontmatter = {
  "title": "Financial Aid",
  "lead": "Financial Aid Lead"
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    h2: "h2",
    li: "li",
    p: "p",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {Figure, HighlightByIndex, IndicatorReference, RelatedMaterial, ScrollyArea, ScrollySection, ScrollySticky} = _components;
  if (!Figure) _missingMdxReference("Figure", true);
  if (!HighlightByIndex) _missingMdxReference("HighlightByIndex", true);
  if (!IndicatorReference) _missingMdxReference("IndicatorReference", true);
  if (!RelatedMaterial) _missingMdxReference("RelatedMaterial", true);
  if (!ScrollyArea) _missingMdxReference("ScrollyArea", true);
  if (!ScrollySection) _missingMdxReference("ScrollySection", true);
  if (!ScrollySticky) _missingMdxReference("ScrollySticky", true);
  return _jsxs(_Fragment, {
    children: [_jsx(Figure, {
      id: "HERO_FINANCE"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "Aid to education is particularly important in low-income countries where it accounts for 12% of total education spending, or 17%, if household education spending is excluded. But it has been rapidly shrinking since 2023, making this a time for tough decisions."
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      id: "aid-trends",
      children: "Aid trends"
    }), "\n", _jsxs(_components.p, {
      children: ["External financing plays a key role in supporting education in the poorest countries. Between 2023 and 2030, the annual SDG 4 financing gap for low- and lower-middle-income countries is estimated at ", _jsx(_components.a, {
        href: "https://www.unesco.org/gem-report/en/can-countries-afford-sdg4-benchmarks",
        children: "USD 97 billion"
      }), ". If all 33 ", _jsx(_components.a, {
        href: "https://www.oecd.org/en/about/committees/development-assistance-committee.html",
        children: "DAC countries"
      }), " had met the ", _jsx(_components.a, {
        href: "https://web-archive.oecd.org/pageViewer?path=/2024-06-17/63452-the07odagnitarget-ahistory.htm&title=The%200.7%25%20ODA/GNI%20target%20-%20a%20history",
        children: "0.7% ODA/GNI target"
      }), " and prioritised education, USD 29 billion more could have been mobilised each year. However, only Denmark, Luxembourg, Norway and Sweden met the target. Total ODA as a share of GNI has declined from 0.37% in 2022-23 to 0.26% in 2025, reaching its lowest level since 2004 and approaching the historical low of 0.21% recorded in 2001."]
    }), "\n", _jsx(_components.p, {
      children: "Amid declining public support, shifting political priorities, and scrutiny of aid effectiveness, development assistance is entering a period of uncertainty. Education may be particularly at risk as several donors reduce future ODA commitments. Since 2015, an increasing share of aid has been redirected to refugee costs and, since 2022, to Ukraine."
    }), "\n", _jsx(_components.p, {
      children: "The decline in aid contrasts with global military spending, which reached USD 2.9 trillion in 2025. Just 1.5 days of military expenditure equalled the entire USD 12.2 billion spent on direct aid to education in 2024."
    }), "\n", _jsx(Figure, {
      id: "ODA_GDP"
    }), "\n", _jsxs(_components.p, {
      children: ["After declining by 6% in 2024, DAC ODA fell by a further 23% in 2025, largely reflecting cuts by the largest bilateral donors. The United States accounted for 75% of the decline, while the five largest donors accounted for 96%. Several donors - including the United Kingdom, Germany, France, Belgium, Canada, the Netherlands and Switzerland - have announced additional reductions. A small number of donors are maintaining or increasing aid, including Italy, Norway, the Republic of Korea, Spain and Sweden.\nThe ", _jsx(HighlightByIndex, {
        index: "0",
        children: "share of education in total aid"
      }), " fell from 8.4% in 2023 to 7.5% in 2024, marking the lowest level recorded since 2010. By contrast, the ", _jsx(HighlightByIndex, {
        index: "2",
        children: "share of governance in total aid"
      }), " has steadily increased, reaching 18% in 2024."]
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "SECTOR_ALLOCABLE"
        })
      }), _jsx(ScrollySection, {
        id: "Health",
        children: _jsxs(_components.p, {
          children: ["As a result of the COVID-19 pandemic, the", " ", "\n", _jsx(HighlightByIndex, {
            index: "1",
            children: "share of health in total aid"
          }), " ", "\nincreased sharply, reaching an all-time high of 24.2% in 2021. It declined to 18.6% in 2024 as emergency support tapered off."]
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Total direct aid to education amounted to USD 12.2 billion in 2024 and has remained essentially constant since 2016, aside from an exceptional uptick in 2022 and 2023, when it reached about USD 13.3 billion. Announced reductions in aid by several donors are expected to drive a sharp decline: aid to education is projected to fall by 30% between 2023 and 2027. ", _jsx(_components.a, {
        href: "https://data-explorer.oecd.org/vis?fs%5B0%5D=Topic%2C1%7CDevelopment%23DEV%23%7COfficial%20Development%20Assistance%20%28ODA%29%23DEV_ODA%23&fs%5B1%5D=Topic%2C2%7CDevelopment%23DEV%23%7COfficial%20Development%20Assistance%20%28ODA%29%23DEV_ODA%23%7CFlows%20on%20individual%20projects%20%28CRS%29%23DEV_ODA_FIP%23&pg=0&fc=Topic&snb=8&df%5Bds%5D=dsDisseminateFinalDMZ&df%5Bid%5D=DSD_CRS%40DF_CRS&df%5Bag%5D=OECD.DCD.FSD&dq=DAC..1000.100._T._T.D.Q._T..&lom=LASTNPERIODS&lo=5&to%5BTIME_PERIOD%5D=false",
        children: "OECD's Credit Reporting System (CRS)"
      }), " data indicate an 8% decline in 2024, while analysis of ", _jsx(_components.a, {
        href: "https://iatistandard.org/en/",
        children: "International Aid Transparency Initiative (IATI)"
      }), " data suggests at least a further 14% decline by 2027. Low-income countries are likely to be hardest hit, with some projected to lose up to half of current aid levels."]
    }), "\n", _jsx(_components.p, {
      children: "Aid to basic education fell by 17% in low-income countries and 18% in lower-middle-income countries between 2023 and 2024, with further declines expected in 2025."
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "EDU_SUB"
        })
      }), _jsx(ScrollySection, {
        id: "absolute,2025,2026,2027",
        children: _jsx(_components.p, {
          children: "Some countries are particularly exposed to ODA cuts. In Rwanda, donor funding accounted for almost 40% of public education spending, compared with 31% in Sierra Leone and 19% in Burundi."
        })
      }), _jsx(ScrollySection, {
        id: "relative",
        children: _jsxs(_components.p, {
          children: ["The share of aid to ", _jsx(HighlightByIndex, {
            index: "0",
            children: "basic education"
          }), " has been slowly declining (from 40% in 2016 to 27% in 2024), losing ground equally to ", _jsx(HighlightByIndex, {
            index: "1",
            children: "secondary"
          }), " and ", _jsx(HighlightByIndex, {
            index: "2",
            children: "post-secondary education"
          }), "."]
        })
      })]
    }), "\n", _jsx(_components.h2, {
      id: "largest-donors",
      children: "Largest donors"
    }), "\n", _jsx(_components.p, {
      children: "The top ten donors disbursed USD 8.9 billion per year between 2022 and 2024, or 79.5% of country programmable aid to education. IDA led with USD 2.5 billion annually, followed by the United States, the EU, Germany and France."
    }), "\n", _jsx(_components.p, {
      children: "Bilateral donors channel only 17% of aid through recipient governments, compared to multilateral donors who channel 68%. So pooled funds, such as the Global Partnership for Education (GPE) and Education Cannot Wait (ECW), play a central role in education."
    }), "\n", _jsx(_components.p, {
      children: "The GPE acts as an intermediary fund, disbursing resources from bilateral donors to country-level partners. Its spending can be tracked through three sources: the OECD CRS database, which documents disbursements from bilateral donors to the GPE; the International Aid Transparency Initiative, and GPE’s own financial reporting, which captures grant utilization. Between 2022 and 2024, CRS data show average annual disbursements to GPE of USD 530 million, while GPE reports average utilization of USD 590 million between 2023 and 2025. GPE disbursements accounted for about 14% of direct aid to basic education during this period, which averaged USD 3.7 billion annually."
    }), "\n", _jsx(Figure, {
      id: "GPE"
    }), "\n", _jsx(_components.p, {
      children: "ECW has consistently published data to IATI. Reported disbursements, expressed in 2022 constant USD, increased from USD 57 million in 2017 to USD 167 million in 2020 and USD 190 million in 2024. Donor-reported contributions in the CRS follow a broadly similar trend, though not identical."
    }), "\n", _jsx(_components.h2, {
      id: "equity",
      children: "Equity"
    }), "\n", _jsx(_components.p, {
      children: "Volume of aid is not the only important consideration. It also matters how effective aid is and how equitably it is allocated. There are several ways to look at equity in aid."
    }), "\n", _jsx(IndicatorReference, {
      id: "4.5.5",
      children: _jsx(_components.p, {
        children: "First, the share of total aid going to education is declining. Between 2010 and 2024, it dropped from 14% to 9% for the World Bank, from 14% to 11% for France, from 10% to 3% for Germany, and from 8% to 3% for Japan. The United States fluctuated between 5% and 9% over the period, ultimately declining to around 5% in 2024, remaining below the global average. While some donors experienced temporary increases in certain years, these gains were not sustained over time. The European Union stands out for a more pronounced rise, increasing from around 5–6% in the mid-2010s to around 10% in recent years."
      })
    }), "\n", _jsx(_components.p, {
      children: "Second, considering that the poorest children are more likely not to even complete primary school, the share of aid allocated to basic education is another indication of equity orientation."
    }), "\n", _jsx(_components.p, {
      children: "The share of basic education in total direct aid fell from 39% in 2016 to 27% in 2024, as other levels gained ground. The United States reduced its share from 84% in 2018 to 66% in 2024, with more aid marked as unspecified. Most donors saw little change or decline over the period."
    }), "\n", _jsx(_components.p, {
      children: "Third, aid should prioritise countries most in need. Yet from 2010 to 2024, lower-middle-income countries received the largest share (48%), while low-income, upper-middle-income, and unspecified groups each received about 20%. Donors vary: the World Bank prioritised lower-middle-income countries (67%), UNRWA upper-middle-income countries (65%), and the UK unspecified income groups (77%)."
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "AID_TO_EDU"
        })
      }), _jsx(ScrollySection, {
        id: "GBR,0",
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(HighlightByIndex, {
            index: "0",
            children: "United Kingdom"
          }), " allocates\n14% of its total aid to education..."]
        })
      }), _jsx(ScrollySection, {
        id: "GBR,1",
        children: _jsxs(_components.p, {
          children: ["...but the share of aid to education that the", " ", "\n", _jsx(HighlightByIndex, {
            index: "0",
            children: "United Kingdom"
          }), " is spending on\nbasic education has fallen from a peak of 62% in 2016 to 47% in 2024 due to its shift in increasing allocation to other education levels."]
        })
      }), _jsx(ScrollySection, {
        id: "GBR,2",
        children: _jsxs(_components.p, {
          children: ["The share of total direct aid to education that the", " ", "\n", _jsx(HighlightByIndex, {
            index: "0",
            children: "United Kingdom"
          }), " allocates to\nlow-income countries fell from 34% in 2010 to 12% in 2024."]
        })
      })]
    }), "\n", _jsx(_components.h2, {
      id: "sub-saharan-africa",
      children: "Sub-Saharan Africa"
    }), "\n", _jsx(_components.p, {
      children: "Comparing regions, the largest recipient of aid to education in 2024 was sub-Saharan Africa (US$4.1 billion), followed by Northern Africa and Western Asia (US$2.6 billion). In total, around a quarter of donors' aid to basic education is allocated to sub-Saharan Africa, home to nearly half of the world's out of school children. In terms of regional distribution, there has been a clear shift of aid to education in recent years to sub-Saharan Africa and the Northern Africa and Western Asia. These two regions accounted for 40% of direct aid to basic education in 2010 and 71% in 2024."
    }), "\n", _jsx(_components.p, {
      children: "The World Bank is the largest donor to education in sub-Saharan Africa. Its support, which fluctuated around US$500 million in the 2000s, more than tripled from US$380 million in 2013 to US$1.2 billion in 2021 and further increased to US$2.0 billion in 2022. Funding has since declined slightly to US$1.8 billion in 2024. The increase was particularly significant in basic education, rising from US$44 million in 2011 to US$256 million in 2020 and peaked at US$673 million in 2022, before falling to US$402 million in 2024. The United Kingdom has reduced their direct funding to the region, but this is because it has shifted funds to support the Global Partnership for Education, which focuses on low-income countries and in particular on Sub-Saharan Africa."
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "AID_EDU_SSA"
        })
      }), _jsx(ScrollySection, {
        id: "USA,FRA",
        children: _jsxs(_components.p, {
          children: [_jsx(HighlightByIndex, {
            index: "0",
            children: "The United States of America"
          }), " and", " ", "\n", _jsx(HighlightByIndex, {
            index: "1",
            children: "France"
          }), " are currently the\nsecond and third largest donors to the region. The United States primarily focused its spending on basic education, while France allocated about 44% of its aid to secondary education."]
        })
      }), _jsx(ScrollySection, {
        id: "World Bank (IDA)",
        children: _jsxs(_components.p, {
          children: [_jsx(HighlightByIndex, {
            index: "2",
            children: "World Bank (IDA)'s"
          }), " aid to\nsub-Saharan Africa since 2022 has been mostly driven by aid to three countries:\nBangladesh, Nigeria and Tanzania."]
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "This flow diagram below of the 8 largest donors in education that provide support to sub-Saharan Africa shows that only a third of direct aid to education goes straight to recipient countries, with the remainder going through donors’ own aid agencies, international and domestic NGOs and multilateral organizations, including GPE and UNICEF. Of the largest donors, only the World Bank channels funds directly to recipient countries while bilateral donors such as the European Union, Germany, the United Kingdom and the United States allocate no more than 20% of aid to recipient countries. As a result, one third of the aid from the top 10 donors goes to unspecified recipient countries."
    }), "\n", _jsx(Figure, {
      id: "AID_FLOWS_GROUPED"
    }), "\n", _jsx(_components.h2, {
      id: "funding-for-education-in-crises",
      children: "Funding for education in crises"
    }), "\n", _jsx(_components.p, {
      children: "Armed conflict, climate shocks and displacement continue to disrupt education, with 122.6 million people forcibly displaced by mid-2024. Education in emergencies and protracted crises (EiEPC) plays a vital role in providing access to learning, protection and essential services, but remains severely underfunded, with only 29% of funding requests met. Donors support EiEPC through both humanitarian and development channels, yet financing remains fragmented across systems like OECD CRS, OCHA FTS and IATI, limiting coordination and strategic planning."
    }), "\n", _jsx(_components.p, {
      children: "CRS and FTS data suggest that EiEPC financing increased rapidly by some USD 700 million in five years: in the case of the CRS, from USD 967 million in 2016 to USD 1.7 billion in 2021; and in the case of the FTS, from USD 564 million in 2016 to almost USD 1.33 billion in 2022. In both cases, it fell by about 20% in the following two years. At the same time, the CRS and the FTS differ considerably in the data they report. CRS aid volumes are one third higher than FTS aid volumes on average. The difference in volume reflects their distinct scopes and methodologies."
    }), "\n", _jsx(Figure, {
      id: "CRS"
    }), "\n", _jsx(_components.p, {
      children: "The share of EiEPC financed through humanitarian channels has declined steadily in the CRS database from 30% financing in 2017 to just 8% in 2023, the remainder coming from development aid channels. In terms of total aid to education, the share of EiEPC has increased from 9% in 2017 to 12% in 2023. When focusing only on countries with humanitarian response plans in place for more than three years, the share rose from 21% to 24% in this period."
    }), "\n", _jsx(Figure, {
      id: "EDU_SCHOL_TT"
    }), "\n", _jsx(_components.h2, {
      id: "conclusion",
      children: "Conclusion"
    }), "\n", _jsxs(_components.p, {
      children: ["Aid is an important source of education financing in low-income countries, which must be analysed jointly with ", _jsx(_components.a, {
        href: "https://www.education-progress.org/en/articles/finance",
        children: "domestic public and private funding"
      }), ". In addition to increasing the funds available for education, equitable distribution needs to be strengthened. In order to achieve the global education goal, SDG 4, it is important that countries and donors take responsibility and fulfil their commitments."]
    }), "\n", _jsx(RelatedMaterial, {
      children: _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://unesdoc.unesco.org/ark:/48223/pf0000398525",
            children: "Counting the loss: Aid to education in 2024 and 2025"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://unesdoc.unesco.org/ark:/48223/pf0000394153",
            children: "Aid to education: time for tough decisions"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://unesdoc.unesco.org/ark:/48223/pf0000394154",
            children: "Funding for education in crises: data in distress"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.unesco.org/gem-report/en/2024efw",
            children: "Education Finance Watch 2024"
          })
        }), "\n"]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
